import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase-client';
import { Save, Bot, AlertCircle, Eye, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { generateTradingStrategy } from '../lib/trading/strategy';
import { placeBybitOrder } from '../lib/api/bybit';

interface StrategySetupProps {
  botId: string;
  onClose?: () => void;
}

function StrategySetup({ botId, onClose }: StrategySetupProps) {
  const [rulesText, setRulesText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatedStrategy, setGeneratedStrategy] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [currentSettings, setCurrentSettings] = useState<any>(null);

  useEffect(() => {
    loadStrategy();
  }, [botId]);

  const loadStrategy = async () => {
    try {
      const { data, error } = await supabase
        .from('trading_bots')
        .select('settings')
        .eq('id', botId)
        .single();

      if (error) throw error;

      // Store current settings
      setCurrentSettings(data?.settings || {});

      if (data?.settings?.strategy) {
        setGeneratedStrategy(data.settings.strategy);
        setRulesText(data.settings.strategy.rules.join('\n'));
      }
    } catch (error) {
      console.error('Error loading strategy:', error);
      toast.error('Failed to load strategy');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateStrategy = async () => {
    try {
      if (!rulesText.trim()) {
        toast.error('Please enter trading rules');
        return;
      }

      // Get bot settings
      const { data: bot, error: botError } = await supabase
        .from('trading_bots')
        .select('settings')
        .eq('id', botId)
        .single();

      if (botError) {
        throw new Error('Failed to load bot settings');
      }

      if (!bot.settings) {
        throw new Error('Bot settings not configured');
      }

      // Generate trading strategy
      const tradingStrategy = await generateTradingStrategy(
        botId,
        rulesText,
        bot.settings
      );

      if (!tradingStrategy) {
        throw new Error('Failed to generate trading strategy');
      }

      const newStrategy = {
        rules: rulesText.split('\n').filter(line => line.trim()),
        tradingStrategy,
        updated_at: new Date().toISOString()
      };

      setGeneratedStrategy(newStrategy);
      setShowPreview(true);
      toast.success('Strategy generated successfully');

      // Test strategy with bot's trading account
      if (bot.settings.trading_account?.api_key && bot.settings.trading_account?.api_secret) {
        try {
          const result = await placeBybitOrder(
            bot.settings.trading_account.api_key,
            bot.settings.trading_account.api_secret,
            tradingStrategy,
            bot.settings.orderBook
          );
          setTestResult(result);
        } catch (testError: any) {
          console.error('Strategy test failed:', testError);
          toast.error('Strategy test failed: ' + (testError.message || 'Unknown error'));
        }
      }
    } catch (error: any) {
      console.error('Error generating strategy:', error);
      toast.error(error.message || 'Failed to generate strategy');
    }
  };

  const handleSave = async () => {
    if (!generatedStrategy) {
      toast.error('Please generate strategy first');
      return;
    }

    setSaving(true);
    try {
      // Merge new strategy with existing settings
      const updatedSettings = {
        ...currentSettings,
        strategy: generatedStrategy
      };

      const { error } = await supabase
        .from('trading_bots')
        .update({
          settings: updatedSettings,
          updated_at: new Date().toISOString()
        })
        .eq('id', botId);

      if (error) throw error;

      toast.success('Strategy saved successfully');
      onClose?.();
    } catch (error) {
      console.error('Error saving strategy:', error);
      toast.error('Failed to save strategy');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-white">Strategy Setup</h3>
        {showPreview && (
          <button
            onClick={() => setShowPreview(false)}
            className="flex items-center space-x-2 text-cyan-400 hover:text-cyan-300"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Editor</span>
          </button>
        )}
      </div>

      {!showPreview ? (
        <div className="bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border border-blue-500/20">
          <div className="mb-6">
            <div className="flex items-center space-x-2 text-yellow-400 mb-4">
              <AlertCircle className="h-5 w-5" />
              <p className="text-sm">Enter your TradingView Pine Script code below. The system will convert it into executable trading rules.</p>
            </div>
            <div className="bg-black/20 rounded p-4 text-sm text-gray-300">
              <p className="font-medium text-white mb-2">Example Pine Script:</p>
              <pre className="text-gray-300 whitespace-pre-wrap">
{`// RSI Strategy
strategy("RSI Strategy", overlay=true)

// Symbol
syminfo.ticker == "BTCUSDT"

// Inputs
rsiLength = input(14, "RSI Length")
rsiOverbought = input(70, "RSI Overbought")
rsiOversold = input(30, "RSI Oversold")
stopLoss = input(2.0, "Stop Loss %")
takeProfit = input(4.0, "Take Profit %")

// Calculate RSI
rsiValue = rsi(close, rsiLength)

// Entry conditions
if (rsiValue < rsiOversold)
    strategy.entry("Long", strategy.long)

// Exit conditions
if (rsiValue > rsiOverbought)
    strategy.exit("Exit Long", "Long", profit = takeProfit, loss = stopLoss)
`}</pre>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Trading Rules
              </label>
              <textarea
                value={rulesText}
                onChange={(e) => setRulesText(e.target.value)}
                className="w-full h-64 px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="Enter your trading rules..."
              />
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleGenerateStrategy}
                className="flex items-center space-x-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors"
              >
                <Eye className="h-5 w-5" />
                <span>Preview Strategy</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border border-blue-500/20">
          <h4 className="text-lg font-medium text-white mb-4">Generated Strategy</h4>
          <div className="space-y-6">
            <div>
              <h5 className="text-sm font-medium text-gray-300 mb-2">Rules</h5>
              <ul className="space-y-2">
                {generatedStrategy.rules.map((rule: string, index: number) => (
                  <li key={index} className="text-gray-300">• {rule}</li>
                ))}
              </ul>
            </div>

            <div>
              <h5 className="text-sm font-medium text-gray-300 mb-2">Trading Strategy</h5>
              <div className="bg-black/20 rounded p-4">
                <pre className="text-sm text-gray-300 overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(generatedStrategy.tradingStrategy, null, 2)}
                </pre>
              </div>
            </div>

            {testResult && (
              <div>
                <h5 className="text-sm font-medium text-gray-300 mb-2">Test Result</h5>
                <div className="bg-black/20 rounded p-4">
                  <pre className="text-sm text-gray-300 overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(testResult, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 text-white hover:text-gray-300 transition-colors"
              >
                Edit Rules
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center space-x-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors"
              >
                <Save className="h-5 w-5" />
                <span>{saving ? 'Saving...' : 'Save Strategy'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StrategySetup;
